import { h, Host } from "@stencil/core";
import { getSvgContent, iconContent } from "./request";
import { getIconRegistry } from "./registry";
import { debounce } from "../../utils/debounce";
/**
 * @slot (default) - not to be used, it's purpose is to prevent icon duplication when
 *                   the web component is cloned.
 */
/**
 * this component is heavily inspired by https://github.com/ionic-team/ionicons
 */
export class Icon {
    constructor() {
        this.name = "";
        this.svgContent = null;
        this.iconDimension = undefined;
        this.fill = "";
        this.filled = false;
        this.sizeS = null;
        this.sizeM = null;
        this.sizeL = null;
        this.weight = null;
    }
    iconElement;
    /** array with the supported values for the fill prop, can be expanded if needed */
    supportedFills = ["primary", "white", "black"];
    tabletMediaWatcher = window.matchMedia("(min-width: 768px)");
    desktopMediaWatcher = window.matchMedia("(min-width: 1024px)");
    hasCustomDimensions = () => {
        return this.sizeS !== null || this.sizeM !== null || this.sizeL !== null;
    };
    updateIconDimension = () => {
        if (!this.hasCustomDimensions()) {
            return;
        }
        if (this.desktopMediaWatcher.matches) {
            // @ts-expect-error Check for typing
            this.iconDimension = this.sizeL || this.sizeM || this.sizeS;
        }
        else if (this.tabletMediaWatcher.matches) {
            // @ts-expect-error Check for typing
            this.iconDimension = this.sizeM || this.sizeS || this.sizeL;
        }
        else {
            // @ts-expect-error Check for typing
            this.iconDimension = this.sizeS || this.sizeM || this.sizeL;
        }
    };
    componentWillLoad() {
        this.updateIconDimension();
        this.loadIcon();
    }
    connectedCallback() {
        this.tabletMediaWatcher.addEventListener("change", this.updateIconDimension);
        this.desktopMediaWatcher.addEventListener("change", this.updateIconDimension);
    }
    disconnectedCallback() {
        this.tabletMediaWatcher.removeEventListener("change", this.updateIconDimension);
        this.desktopMediaWatcher.removeEventListener("change", this.updateIconDimension);
    }
    /** Reload asset – called from the `IconRegistry` when the `assetsPath` has changed */
    async reload() {
        this.loadIconDebounced();
    }
    updateIcon() {
        this.loadIconDebounced();
    }
    loadIcon = () => {
        if (this.name === "") {
            // @ts-expect-error Check for typing
            this.svgContent = null;
            return;
        }
        const url = getIconRegistry().resolveAssetUrl(this);
        if (url === null) {
            // @ts-expect-error Check for typing
            this.svgContent = null;
            if (loggedWarnings.includes(this.name)) {
                return;
            }
            /* eslint-disable-next-line no-console */
            console.warn(`Icon ${this.name} is not available in ux-icon.`);
            loggedWarnings.push(this.name);
            return;
        }
        if (iconContent.has(url)) {
            // sync if it's already loaded
            // @ts-expect-error Check for typing
            this.svgContent = iconContent.get(url);
        }
        else {
            // async if it hasn't been loaded
            getSvgContent(url)
                // @ts-expect-error Check for typing
                .then(() => (this.svgContent = iconContent.get(url)))
                .catch(() => {
                /* eslint-disable-next-line no-console */
                console.warn(`Icon ${this.name} could not be loaded from ${url}`);
            });
        }
    };
    loadIconDebounced = debounce(this.loadIcon, 100);
    render() {
        if (this.name === "") {
            return null;
        }
        const customCss = this.hasCustomDimensions() ? { fontSize: `${this.iconDimension}px` } : null;
        return (h(Host, { class: {
                ["ux-icon"]: true,
                [`ux-icon--${this.fill}`]: this.supportedFills.includes(this.fill)
            },
            // @ts-expect-error Check for typing
            style: customCss }, h("div", { class: "ux-icon__inner", innerHTML: this.svgContent }), h("div", { class: "ux-icon__slot" }, h("slot", null))));
    }
    static get is() { return "ux-icon"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["icon.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["icon.css"]
        };
    }
    static get properties() {
        return {
            "name": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "name of icon"
                },
                "attribute": "name",
                "reflect": true,
                "defaultValue": "\"\""
            },
            "fill": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "property to change the color of the icon, supported colors: green, white, black"
                },
                "attribute": "fill",
                "reflect": false,
                "defaultValue": "\"\""
            },
            "filled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "if set, the filled version (if available) will be selected"
                },
                "attribute": "filled",
                "reflect": false,
                "defaultValue": "false"
            },
            "sizeS": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number | null",
                    "resolved": "null | number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "the size of the icon on mobile viewports"
                },
                "attribute": "size-s",
                "reflect": false,
                "defaultValue": "null"
            },
            "sizeM": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number | null",
                    "resolved": "null | number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "the size of the icon on tablet viewports"
                },
                "attribute": "size-m",
                "reflect": false,
                "defaultValue": "null"
            },
            "sizeL": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number | null",
                    "resolved": "null | number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "the size of the icon on desktop viewports"
                },
                "attribute": "size-l",
                "reflect": false,
                "defaultValue": "null"
            },
            "weight": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "weight of the icon, either \"light\", \"normal\", or \"bold\", defaults to normal"
                },
                "attribute": "weight",
                "reflect": false,
                "defaultValue": "null"
            }
        };
    }
    static get states() {
        return {
            "svgContent": {},
            "iconDimension": {}
        };
    }
    static get methods() {
        return {
            "reload": {
                "complexType": {
                    "signature": "() => Promise<void>",
                    "parameters": [],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Reload asset \u2013 called from the `IconRegistry` when the `assetsPath` has changed",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "iconElement"; }
    static get watchers() {
        return [{
                "propName": "name",
                "methodName": "updateIcon"
            }, {
                "propName": "filled",
                "methodName": "updateIcon"
            }, {
                "propName": "weight",
                "methodName": "updateIcon"
            }];
    }
}
// this array holds logged warnings about unknown icons to prevent duplicate warnings
const loggedWarnings = [];
//# sourceMappingURL=icon.js.map
